import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileTextOutlined,
  FileWordOutlined,
  InboxOutlined,
  LinkOutlined,
  PictureOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api, { API_URL } from '../config/api';
import { textLocaleTag } from '../i18n/locale';
import { useAuthStore } from '../store/authStore';

const BASE_URL = API_URL.replace('/api', ''); // Get base URL without /api suffix
const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Dragger } = Upload;

interface Document {
  id: string;
  title: string;
  description?: string;
  url: string;
  type: string;
  uploadedAt: string;
  eventId: string;
  Event?: {
    id: string;
    title: string;
    Organization?: {
      name: string;
    };
  };
}

interface Event {
  id: string;
  title: string;
  organizationId: string;
}

const documentTypeConfig = {
  PRESENTATION: {
    color: 'blue',
    labelKey: 'documents.types.presentation',
    icon: <FilePptOutlined />,
  },
  BROCHURE: { color: 'green', labelKey: 'documents.types.brochure', icon: <FileTextOutlined /> },
  AGENDA: { color: 'purple', labelKey: 'documents.types.agenda', icon: <FileTextOutlined /> },
  MAP: { color: 'orange', labelKey: 'documents.types.map', icon: <EnvironmentOutlined /> },
  OTHER: { color: 'default', labelKey: 'documents.types.other', icon: <FileTextOutlined /> },
} as const;

const getFileIcon = (url: string) => {
  const ext = url.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return <FilePdfOutlined style={{ fontSize: 20 }} />;
    case 'doc':
    case 'docx':
      return <FileWordOutlined style={{ fontSize: 20 }} />;
    case 'xls':
    case 'xlsx':
      return <FileExcelOutlined style={{ fontSize: 20 }} />;
    case 'ppt':
    case 'pptx':
      return <FilePptOutlined style={{ fontSize: 20 }} />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return <PictureOutlined style={{ fontSize: 20 }} />;
    default:
      return <FileTextOutlined style={{ fontSize: 20 }} />;
  }
};

const Documents: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>();
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);
  const [form] = Form.useForm();
  const { user } = useAuthStore();

  useEffect(() => {
    fetchEvents();
    fetchDocuments();
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      const params: any = {};
      if (user?.organizationId) {
        params.organizationId = user.organizationId;
      }
      const response = await api.get('/events', { params });
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (selectedEvent) {
        params.eventId = selectedEvent;
      } else if (user?.organizationId) {
        params.organizationId = user.organizationId;
      }
      const response = await api.get('/documents', { params });
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingDocument(null);
    form.resetFields();
    setUploadedFiles([]);
    setFileList([]);
    setModalVisible(true);
  };

  const handleEdit = (document: Document) => {
    setEditingDocument(document);
    form.setFieldsValue(document);
    setUploadedFiles([]);
    setFileList([]);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/documents/${id}`);
      message.success(t('documents.deleted'));
      fetchDocuments();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('messages.delete_error'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // Ha új dokumentumokat töltünk fel
      if (!editingDocument && uploadedFiles.length > 0) {
        // Minden feltöltött fájlhoz létrehozunk egy dokumentumot
        const promises = uploadedFiles.map(async (file) => {
          const data = {
            ...values,
            url: `${BASE_URL}${file.url}`,
            title: values.title || file.originalName.split('.')[0], // Ha nincs cím, használjuk a fájlnevet
          };
          console.log('Sending document data:', data);
          return api.post('/documents', data);
        });

        await Promise.all(promises);
        message.success(t('documents.documentsCreated', { count: uploadedFiles.length }));
      } else if (editingDocument) {
        // Szerkesztésnél vagy megtartjuk a régi URL-t, vagy az új feltöltöttet használjuk
        // Remove eventId from values when updating
        const { eventId, ...updateData } = values;
        const data = {
          ...updateData,
          url:
            uploadedFiles.length > 0 ? `${BASE_URL}${uploadedFiles[0].url}` : editingDocument.url,
        };
        await api.patch(`/documents/${editingDocument.id}`, data);
        message.success(t('documents.updated'));
      } else {
        message.error(t('documents.fileRequired'));
        return;
      }

      setModalVisible(false);
      form.resetFields();
      setUploadedFiles([]);
      setFileList([]);
      fetchDocuments();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('documents.operationFailed'));
    }
  };

  const columns: ColumnsType<Document> = [
    {
      title: t('documents.document_type'),
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => {
        const config =
          documentTypeConfig[type as keyof typeof documentTypeConfig] || documentTypeConfig.OTHER;
        return (
          <Tag color={config.color} icon={config.icon}>
            {t(config.labelKey)}
          </Tag>
        );
      },
      filters: Object.entries(documentTypeConfig).map(([key, value]) => ({
        text: t(value.labelKey),
        value: key,
      })),
      onFilter: (value, record) => record.type === value,
    },
    {
      title: t('documents.fileColumn'),
      key: 'file',
      render: (_, record) => (
        <Space>
          {getFileIcon(record.url)}
          <span>{record.title}</span>
        </Space>
      ),
    },
    {
      title: t('common.event'),
      dataIndex: ['Event', 'title'],
      key: 'event',
      render: (title: string) => title || '-',
    },
    {
      title: t('documents.uploaded_at'),
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      render: (date: string) => new Date(date).toLocaleDateString(textLocaleTag(i18n.language)),
      sorter: (a, b) => new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime(),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <a href={record.url} target="_blank" rel="noopener noreferrer">
            <Button type="text" icon={<DownloadOutlined />} />
          </a>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title={t('common.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2}>{t('documents.title')}</Title>
          <Space>
            <Select
              style={{ width: 250 }}
              placeholder={t('common.allEvents')}
              allowClear
              value={selectedEvent}
              onChange={setSelectedEvent}
            >
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('documents.new_document')}
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={documents}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => t('documents.paginationTotal', { count: total }),
          }}
        />
      </Space>

      <Modal
        title={editingDocument ? t('documents.edit_document') : t('documents.newUpload')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setUploadedFiles([]);
          setFileList([]);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="eventId"
            label={t('common.event')}
            rules={[{ required: true, message: t('common.selectEventRequired') }]}
          >
            <Select placeholder={t('common.selectEvent')}>
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="type"
            label={t('documents.document_type')}
            rules={[{ required: true, message: t('documents.typeRequired') }]}
          >
            <Select>
              <Option value="PRESENTATION">{t('documents.types.presentation')}</Option>
              <Option value="BROCHURE">{t('documents.types.brochure')}</Option>
              <Option value="AGENDA">{t('documents.types.agenda')}</Option>
              <Option value="MAP">{t('documents.types.map')}</Option>
              <Option value="OTHER">{t('documents.types.other')}</Option>
            </Select>
          </Form.Item>

          {!editingDocument && (
            <Form.Item label={t('documents.uploadFiles')} extra={t('documents.supportedFormats')}>
              <div
                style={{
                  textAlign: 'center',
                  padding: 32,
                  border: '1px dashed #d9d9d9',
                  borderRadius: 8,
                }}
              >
                <input
                  type="file"
                  id="file-input"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const files = e.target.files;
                    if (!files) return;

                    const formData = new FormData();
                    Array.from(files).forEach((file) => {
                      formData.append('documents', file);
                    });

                    try {
                      const response = await api.post('/upload/documents', formData, {
                        headers: {
                          'Content-Type': 'multipart/form-data',
                        },
                      });

                      setUploadedFiles(response.data);
                      message.success(t('documents.filesUploaded', { count: files.length }));
                    } catch (error: any) {
                      message.error(
                        t('documents.uploadFailed', {
                          reason: error.response?.data?.message || error.message,
                        })
                      );
                    }
                  }}
                />
                <Button
                  icon={<UploadOutlined />}
                  size="large"
                  onClick={() => {
                    console.log('Button clicked - NEW VERSION WITH API!');
                    document.getElementById('file-input')?.click();
                  }}
                >
                  {t('documents.selectFilesButton')}
                </Button>
                <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                  {t('documents.dragHint')}
                </div>
              </div>
              {uploadedFiles.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <strong>{t('documents.uploadedFilesLabel')}</strong>
                  <ul>
                    {uploadedFiles.map((file, index) => (
                      <li key={index}>{file.originalName || file.filename}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Form.Item>
          )}

          {editingDocument && (
            <Form.Item label={t('documents.replaceFile')} extra={t('documents.replaceFileHelp')}>
              <div>
                <input
                  type="file"
                  id="edit-file-input"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    const formData = new FormData();
                    formData.append('document', file);

                    try {
                      const response = await api.post('/upload/document', formData, {
                        headers: {
                          'Content-Type': 'multipart/form-data',
                        },
                      });

                      setUploadedFiles([response.data]);
                      message.success(t('documents.fileUploaded'));
                    } catch (error: any) {
                      message.error(
                        t('documents.uploadFailed', {
                          reason: error.response?.data?.message || error.message,
                        })
                      );
                    }
                  }}
                />
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => document.getElementById('edit-file-input')?.click()}
                >
                  {t('documents.chooseNewFile')}
                </Button>
              </div>
            </Form.Item>
          )}

          <Form.Item
            name="title"
            label={t('documents.titleOptional')}
            help={!editingDocument ? t('documents.titleHelp') : ''}
          >
            <Input placeholder={t('documents.titlePlaceholder')} />
          </Form.Item>

          <Form.Item name="description" label={t('documents.descriptionOptional')}>
            <TextArea rows={3} placeholder={t('documents.descriptionPlaceholder')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingDocument ? t('common.update') : t('documents.upload')}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setUploadedFiles([]);
                  setFileList([]);
                }}
              >
                {t('documents.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Documents;
